import { Routes } from '@angular/router';
import { FeatureFlagGuard } from '@core/guards/feature-flag.guard';
import { ResourceGuard } from '@core/guards/resource.guard';
import { AuthGuardService } from '@rockwell-automation-inc/service';
import { AuthCallbackComponent } from './v2/auth/auth-callback/auth-callback.component';
import { LandingComponent } from './v2/auth/landing/landing.component';
import { ErrorLoginComponent } from './v2/error-login/error-login.component';
import { canManageAny, canManageTenant } from './v2/shared/utils';
import { Shell } from './v2/shell/shell.service';
import { CreateOrganizationComponent } from './v2/standalone/organizations/create/create-organization.component';
import { SubOrganizationComponent } from './v2/standalone/organizations/create/sub-organization/sub-organization.component';
import { JoinOrganizationComponent } from './v2/standalone/organizations/join/join-organization.component';
import { genCanDeactivateFn } from './v2/standalone/organizations/settings/pending-changes-guard';
import { SettingsComponent } from './v2/standalone/organizations/settings/settings.component';

export const appRoutes: Routes = [
  { component: LandingComponent, path: 'sign-in' },
  { component: LandingComponent, path: 'linklogin/:resid/:slug' },
  { component: AuthCallbackComponent, path: 'callback' },
  {
    path: '',
    pathMatch: 'prefix',
    redirectTo: 'dashboard',
  },
  {
    canActivate: [AuthGuardService],
    loadComponent: () => import('./v2/eula/eula.component').then((m) => m.EulaComponent),
    path: 'eula',
  },
  {
    component: ErrorLoginComponent,
    path: 'error/login',
  },
  // { path: '**', redirectTo: 'dashboard'},
  Shell.childRoutes([
    {
      children: [
        {
          canActivate: [AuthGuardService],
          loadChildren: () => import('./v2/dashboard/dashboard-routing.module').then((m) => m.dashboardRoutes),
          path: 'dashboard',
        },
        {
          canActivate: [AuthGuardService],
          path: 'organizations',
          children: [
            {
              path: '',
              loadComponent: () =>
                import('./v2/standalone/organizations/parent-org-list/parent-org-list.component').then(
                  (m) => m.ParentOrgListComponent,
                ),
            },
            {
              canActivate: [AuthGuardService],
              loadChildren: () => import('./v2/dashboard/dashboard-routing.module').then((m) => m.dashboardRoutes),
              path: ':orgId/dashboard',
            },
            {
              path: ':orgId/suborgs',
              data: { canManage: canManageAny },
              loadComponent: () =>
                import('./v2/standalone/organizations/sub-org-list/sub-org-list.component').then(
                  (m) => m.SubOrgListComponent,
                ),
            },
            {
              component: SubOrganizationComponent,
              canActivate: [ResourceGuard],
              data: {
                canManage: canManageTenant,
                breadcrumb: { label: 'Create sub-organization', suppressChildTenant: true },
              },
              path: ':orgId/create',
            },
            {
              canActivate: [ResourceGuard],
              canDeactivate: [
                genCanDeactivateFn<SettingsComponent>(
                  (component) =>
                    component.$tenantContext() !== 'NotSelected' &&
                    (component.formChangeDetected || component.logoChangeDetected),
                ),
              ],
              data: { canManage: canManageTenant },
              path: ':orgId/settings',
              loadComponent: () =>
                import('./v2/standalone/organizations/settings/settings.component').then((m) => m.SettingsComponent),
            },
            {
              component: CreateOrganizationComponent,
              data: { breadcrumb: { label: 'Create Organization', suppressTenant: true } },
              path: 'create',
            },
            {
              component: JoinOrganizationComponent,
              data: { breadcrumb: { label: 'Join Organization', suppressTenant: true } },
              path: 'join',
            },
            {
              loadChildren: () =>
                import('./v2/entitlement/entitlement-routing.module').then((m) => m.entitlementRoutes),
              path: ':orgId/entitlement',
            },
            {
              canActivate: [ResourceGuard],
              data: { canManage: canManageAny },
              loadChildren: () =>
                import('./v2/invite-users/invite-users-routing.module').then((m) => m.inviteUsersRoutes),
              path: ':orgId/invite-users',
            },
            {
              canActivate: [ResourceGuard],
              data: { canManage: canManageAny },
              loadChildren: () =>
                import('./v2/access-management/access-management-routing.module').then((m) => m.accessManagementRoutes),
              path: ':orgId/access-mngmt',
            },
            {
              canActivate: [ResourceGuard],
              data: { canManage: canManageAny },
              loadChildren: () =>
                import('./v2/approve-users/approve-users-routing.module').then((m) => m.approveUsersRoutes),
              path: ':orgId/approve-users',
            },
            { path: '**', redirectTo: '', pathMatch: 'full' },
          ],
        },
        {
          loadChildren: () => import('./v2/entitlement/entitlement-routing.module').then((m) => m.entitlementRoutes),
          path: 'entitlement',
        },
        {
          canActivate: [AuthGuardService, FeatureFlagGuard],
          data: { featureFlagName: 'enable_user_assigned_trials', redirectPath: '/entitlement' },
          loadChildren: () =>
            import('./v2/entitlement/my-entitlements-routing.module').then((m) => m.myEntitlementsRoutes),
          path: 'my-entitlements',
        },
        {
          canActivate: [AuthGuardService],
          loadChildren: () => import('./v2/trials/trials-routing.module').then((m) => m.trialsRoutes),
          path: 'campaigns',
        },
        {
          canActivate: [AuthGuardService],
          loadChildren: () => import('./v2/join-request/join-request-routing.module').then((m) => m.joinRequestRoutes),
          path: 'request/:id',
        },
        {
          canActivate: [ResourceGuard],
          data: { canManage: canManageAny },
          loadChildren: () =>
            import('./v2/access-management/access-management-routing.module').then((m) => m.accessManagementRoutes),
          path: 'access-mngmt',
        },
        {
          canActivate: [ResourceGuard],
          data: { canManage: canManageTenant },
          loadChildren: () =>
            import('./v2/utility-tokens/utility-tokens-routing.module').then((m) => m.utilityTokensRoutes),
          path: 'universalcredits/ledger',
        },
        {
          loadChildren: () => import('./v2/error/error-routing.module').then((m) => m.errorRoutes),
          path: 'error',
        },
        {
          loadChildren: () => import('./v2/about/about-routing.module').then((m) => m.aboutRoutes),
          path: 'about',
        },
      ],
      path: '',
    },
  ]),
  { path: '**', redirectTo: '/dashboard' },
];
